import React, { useEffect, useCallback, useMemo, useState } from 'react';
import { Form, Button, Row, Col, Card } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faSave,
    faTimes,
    faArrowLeft,
    faClose,
    faPlus,
    faExchangeAlt,
    faStore,
    faBoxes,
    faCheckCircle,
    faExclamationTriangle,
    faClipboardList,
    faEdit,
    faInfoCircle
} from '@fortawesome/free-solid-svg-icons';
import { Link, useHistory, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import AsyncSelect from 'react-select/async';
import { useForm, Controller, useFieldArray, useWatch } from 'react-hook-form';
import { debounce } from 'lodash';

import { getProductList, getProductDetails, getMeasurementUnits } from '../Controller/ProductController';
// import { getStoreList } from '../Controller/StoreController';
import { createStockTransfer, getStockTransferDetails, updateStockTransfer, getStoreList } from '../Controller/StockTransferController';
import LoginUtility from "../../../../GlobalModules/Utility/Utility";
import { connect } from 'react-redux';

const defaultValues = {
    fromStore: null,
    toStore: null,
    notes: '',
    productSearch: null,
    // keep a single blank row for manual entry; the top search will now REUSE it
    selectedProducts: [
        { value: null, name: '', product_code: '', label: null, quantity: '', quantityLeft: null, quantity_requested: null, requested_unit_of_measure: null, unitOfMeasure: '', uom_id: null, uom_mappings: [], is_from_requisition: false },
    ],
};

// --- Layout constants ---
const FIELD_HEIGHT = 34;      // Compact height of inputs/selects

// Compact error wrapper without reserving empty blank space when there is no error
const FieldWithUnderError = ({ children, error }) => (
    <div style={{ position: 'relative' }}>
        {children}
        {error && (
            <div
                style={{
                    color: '#dc3545',
                    fontSize: '0.75rem',
                    fontWeight: 500,
                    marginTop: 2,
                    lineHeight: '14px',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                }}
                title={error || ''}
            >
                {error}
            </div>
        )}
    </div>
);

// react-select compact inline styles
const selectStyles = (hasError) => ({
    control: (base, state) => ({
        ...base,
        minHeight: FIELD_HEIGHT,
        height: FIELD_HEIGHT,
        borderRadius: 6,
        borderColor: hasError ? '#dc3545' : state.isFocused ? '#3b82f6' : '#cbd5e1',
        boxShadow: hasError
            ? '0 0 0 .2rem rgba(220,53,69,.2)'
            : state.isFocused
                ? '0 0 0 .2rem rgba(59,130,246,.2)'
                : 'none',
        backgroundColor: state.isDisabled ? '#f8fafc' : base.backgroundColor,
        cursor: state.isDisabled ? 'not-allowed' : base.cursor,
        transition: 'all 0.15s ease',
        '&:hover': {
            borderColor: hasError
                ? '#dc3545'
                : (state.isFocused ? '#3b82f6' : '#94a3b8'),
        },
    }),
    singleValue: (base, state) => ({
        ...base,
        color: state.isDisabled ? '#64748b' : '#1e293b',
        fontWeight: 500,
        fontSize: '0.825rem',
    }),
    placeholder: (base) => ({
        ...base,
        color: '#94a3b8',
        fontSize: '0.825rem',
    }),
    indicatorsContainer: (base) => ({ ...base, height: FIELD_HEIGHT }),
    dropdownIndicator: (base) => ({ ...base, padding: '2px 6px' }),
    clearIndicator: (base) => ({ ...base, padding: '2px 6px' }),
    valueContainer: (base) => ({ ...base, height: FIELD_HEIGHT, padding: '0 8px' }),
    input: (base) => ({ ...base, margin: 0, padding: 0 }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    menu: (base) => ({
        ...base,
        borderRadius: 6,
        boxShadow: '0 8px 20px -4px rgba(0, 0, 0, 0.1)',
        border: '1px solid #e2e8f0',
        zIndex: 9999,
    }),
    option: (base, state) => ({
        ...base,
        backgroundColor: state.isSelected ? '#2563eb' : state.isFocused ? '#eff6ff' : 'white',
        color: state.isSelected ? 'white' : '#1e293b',
        cursor: 'pointer',
        fontSize: '0.825rem',
        padding: '6px 10px',
    }),
});

const StockTransferForm = ({ isEdit = false, currentStore }) => {
    const history = useHistory();
    const { slack } = useParams();
    const [requisitionReference, setRequisitionReference] = useState('');
    const [requisitionSlack, setRequisitionSlack] = useState('');
    const [requisitionId, setRequisitionId] = useState('');
    const [measurementUnits, setMeasurementUnits] = useState([]);

    // useEffect(() => {
    //     const fetchUnits = async () => {
    //         try {
    //             const res = await getMeasurementUnits();
    //             if (res.success && res.data && Array.isArray(res.data)) {
    //                 setMeasurementUnits(res.data);
    //             }
    //         } catch (err) {
    //             console.error("Error fetching measurement units:", err);
    //         }
    //     };
    //     fetchUnits();
    // }, []);

    const {
        control,
        handleSubmit,
        setValue,
        reset,
        getValues,
        clearErrors,
        trigger,
        formState: { errors, isSubmitting },
    } = useForm({ defaultValues, mode: 'onChange' });

    const { fields, append, remove, update } = useFieldArray({
        control,
        name: 'selectedProducts',
        keyName: 'key',
    });

    // 👀 Live watch of the array values (fixes stale `fields` snapshots)
    const watchedRows = useWatch({ control, name: 'selectedProducts' });

    const isFromRequisitionMode = !!requisitionReference;

    const fetchStockTransfer = async () => {
        try {
            const response = await getStockTransferDetails(slack);
            if (response.success && response.data) {
                const stockTransfer = response.data;
                if (stockTransfer.stock_transfer_reference) {
                    setRequisitionReference(stockTransfer.stock_transfer_reference);
                }
                if (stockTransfer.requisition_id) {
                    setRequisitionId(stockTransfer.requisition_id);
                }

                reset({
                    fromStore: {
                        label: stockTransfer.StockTransfer_FromStore.name,
                        value: stockTransfer.StockTransfer_FromStore.id,
                        store_code: stockTransfer.StockTransfer_FromStore.store_code,
                    },
                    toStore: {
                        label: stockTransfer.StockTransfer_ToStore.name,
                        value: stockTransfer.StockTransfer_ToStore.id,
                        store_code: stockTransfer.StockTransfer_ToStore.store_code,
                    },
                    notes: stockTransfer.notes,
                    selectedProducts: stockTransfer.transfer_products.map((el) => {
                        const uomMappings = Array.isArray(el.uom_mappings) && el.uom_mappings.length > 0
                            ? el.uom_mappings
                            : (Array.isArray(el.Product?.uom_mappings) && el.Product.uom_mappings.length > 0 ? el.Product.uom_mappings : []);
                        return {
                            value: el.product_slack,
                            name: el.product_name || el.Product?.name || 'Product',
                            product_code: el.Product?.product_code || el.product_code || '',
                            label: el.product_name,
                            quantity: el.quantity ?? '',
                            quantityLeft: el.Product?.quantity,
                            quantity_requested: el.quantity_requested || null,
                            requested_unit_of_measure: el.requested_unit_of_measure || null,
                            unitOfMeasure: el.unit_of_measure || el.Product?.unit_of_measure || '',
                            uom_id: el.uom_id || el.Product?.base_uom_id || null,
                            uom_mappings: uomMappings,
                            base_uom_id: el.Product?.base_uom_id,
                            base_unit_of_measure: el.Product?.unit_of_measure,
                            meta_data: el.meta_data || el.Product?.meta_data || [],
                            is_from_requisition: !!stockTransfer.requisition_id || !!stockTransfer.stock_transfer_reference,
                        };
                    }),
                    productSearch: null,
                });
            } else {
                toast.error('Stock transfer not found');
            }
        } catch (error) {
            console.error('Error fetching stock transfer:', error);
            toast.error('Failed to fetch stock transfer details');
        }
    };

    const fetchProductOptions = async (inputValue) => {
        try {
            const response = await getProductList({ limit: 10, offset: 0, search: inputValue, store_id: localStorage.getItem('store_id'), is_ingredient: 1, own_store: localStorage.getItem('store_id'), });
            if (response.success && response.data) {
                return response.data.map((product) => {
                    let labelParts = [product.name];

                    if (product.product_code) {
                        labelParts.push(`Code: ${product.product_code}`);
                    }

                    if (product.quantity !== undefined && product.quantity !== null) {
                        labelParts.push(`Qty: ${product.quantity}${product.unit_of_measure ? ` ${product.unit_of_measure}` : ''}`);
                    }

                    const mappings = Array.isArray(product.uom_mappings) ? product.uom_mappings : [];
                    const baseMapping = mappings.find(u => u.is_base === 1 || u.is_base === true) || mappings[0];
                    const defaultUom = baseMapping ? (baseMapping.unit_code || baseMapping.symbol || baseMapping.label) : (product.unit_of_measure || '');
                    const defaultUomId = baseMapping ? (baseMapping.uom_id || baseMapping.id) : (product.base_uom_id || product.uom_id || null);

                    return {
                        value: product.slack,
                        name: product.name,
                        product_code: product.product_code || '',
                        label: labelParts.join(' | '),
                        unitPrice: product.purchase_amount_excluding_tax,
                        tax: product.TaxCode?.total_tax_percentage && product.TaxCode?.total_tax_percentage != "" ? product.TaxCode?.total_tax_percentage : "",
                        quantityLeft: product.quantity,
                        unitOfMeasure: defaultUom,
                        uom_id: defaultUomId ? Number(defaultUomId) : null,
                        base_uom_id: product.base_uom_id,
                        base_unit_of_measure: product.unit_of_measure,
                        uom_mappings: mappings,
                        meta_data: product.meta_data,
                        is_from_requisition: false,
                    };
                });
            }
            return [];
        } catch (e) {
            console.error('Error loading products:', e);
            return [];
        }
    };

    const debouncedFetchProductOptions = useMemo(
        () => debounce((inputValue, callback) => { fetchProductOptions(inputValue).then(callback); }, 400),
        [] // eslint-disable-line react-hooks/exhaustive-deps
    );

    const fetchStoreOptions = async (inputValue) => {
        try {
            const response = await getStoreList({ limit: 10, offset: 0, search: inputValue, all: true });
            if (response.success && response.data) {
                return response.data
                    .filter((store) => store.id !== Number(localStorage.getItem('store_id'))) // exclude current store
                    .map((store) => ({
                        value: store.id,
                        label: store.name,
                        store_code: store.store_code,
                    }));
            }
            return [];
        } catch (e) {
            console.error('Error loading stores:', e);
            return [];
        }
    };

    const debouncedFetchStoreOptions = useMemo(
        () => debounce((inputValue, callback) => { fetchStoreOptions(inputValue).then(callback); }, 400),
        [] // eslint-disable-line react-hooks/exhaustive-deps
    );

    const fetchCurrentStore = useCallback(async () => {
        try {
            const response = await getStoreList();
            if (response.success && response.data) {
                const currentStoreId = localStorage.getItem('store_id');
                let currentStore = response.data.find((s) => s.id === +currentStoreId);
                if (!currentStore) {
                    for (const store of response.data) {
                        if (store.child_stores && store.child_stores.length > 0) {
                            currentStore = store.child_stores.find((cs) => cs.id === +currentStoreId);
                            if (currentStore) break;
                        }
                    }
                }
                if (currentStore) {
                    setValue('fromStore', {
                        label: currentStore.name,
                        value: currentStore.id,
                        store_code: currentStore.store_code,
                    }, { shouldValidate: true });
                } else {
                    toast.error('Failed to find current store');
                }
            } else {
                toast.error('Failed to fetch current store');
            }
        } catch (e) {
            console.error('Error fetching from store:', e);
            toast.error('An error occurred while fetching current store');
        }
    }, [setValue]);

    useEffect(() => { fetchCurrentStore(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const handleProductPickedFromTop = async (option) => {
        if (!option) {
            setValue('productSearch', null, { shouldValidate: true });
            return;
        }

        const rows = getValues('selectedProducts') || [];

        // if product already exists: increment its quantity
        const existingIdx = rows.findIndex((p) => p?.value === option.value);
        if (existingIdx !== -1) {
            const existing = rows[existingIdx] || {};
            const prevQ =
                existing.quantity === '' || existing.quantity == null
                    ? 0
                    : typeof existing.quantity === 'number'
                        ? existing.quantity
                        : parseFloat(existing.quantity);

            const nextQ = Number.isFinite(prevQ) ? prevQ + 1 : 1;

            update(existingIdx, { ...existing, quantity: nextQ });
            clearErrors(`selectedProducts.${existingIdx}.quantity`);
            setValue('productSearch', null, { shouldValidate: true });
            return;
        }

        const mappings = Array.isArray(option.uom_mappings) ? option.uom_mappings : [];
        const baseMapping = mappings.find(u => u.is_base === 1 || u.is_base === true) || mappings[0];
        const defaultUom = baseMapping ? (baseMapping.unit_code || baseMapping.symbol || baseMapping.label) : (option.unitOfMeasure || '');
        const defaultUomId = baseMapping ? (baseMapping.uom_id || baseMapping.id) : (option.uom_id || option.base_uom_id || null);

        const prodName = option.name || (option.label ? option.label.split(' | ')[0] : 'Product');
        const prodCode = option.product_code || (option.label?.includes('Code: ') ? option.label.split('Code: ')[1]?.split(' | ')[0] : '');

        const productData = {
            ...option,
            value: option.value,
            name: prodName,
            product_code: prodCode,
            quantity: 1,
            unitOfMeasure: defaultUom,
            uom_id: defaultUomId ? Number(defaultUomId) : null,
            uom_mappings: mappings,
            base_uom_id: option.base_uom_id || (baseMapping ? (baseMapping.uom_id || baseMapping.id) : null),
            base_unit_of_measure: defaultUom,
            is_from_requisition: false,
        };

        // find first truly empty row (no product chosen)
        const emptyIdx = rows.findIndex((r) => !r?.value);

        if (emptyIdx !== -1) {
            update(emptyIdx, {
                ...rows[emptyIdx],
                value: option.value,
                name: prodName,
                product_code: prodCode,
                label: option.label,
                quantityLeft: option.quantityLeft ?? null,
                unitOfMeasure: defaultUom,
                uom_id: defaultUomId ? Number(defaultUomId) : null,
                uom_mappings: mappings,
                base_uom_id: option.base_uom_id || (baseMapping ? (baseMapping.uom_id || baseMapping.id) : null),
                base_unit_of_measure: defaultUom,
                is_from_requisition: false,
                quantity:
                    rows[emptyIdx].quantity === '' || rows[emptyIdx].quantity == null
                        ? 1
                        : rows[emptyIdx].quantity,
                meta_data: option.meta_data,
            });
            clearErrors(`selectedProducts.${emptyIdx}.quantity`);
            clearErrors(`selectedProducts.${emptyIdx}.unitOfMeasure`);
        } else {
            append(productData);
        }

        setValue('productSearch', null, { shouldValidate: true });
    };

    const applyRowProduct = async (option, index) => {
        const rows = getValues('selectedProducts') || [];
        const current = rows[index] || {};

        if (!option) {
            update(index, {
                ...current,
                value: null,
                name: '',
                product_code: '',
                label: null,
                quantityLeft: null,
                unitOfMeasure: '',
                uom_id: null,
                uom_mappings: [],
                quantity: '',
                meta_data: null,
                is_from_requisition: false,
            });
            clearErrors(`selectedProducts.${index}.quantity`);
            clearErrors(`selectedProducts.${index}.unitOfMeasure`);
            return;
        }

        const mappings = Array.isArray(option.uom_mappings) ? option.uom_mappings : [];
        const baseMapping = mappings.find(u => u.is_base === 1 || u.is_base === true) || mappings[0];
        const defaultUom = baseMapping ? (baseMapping.unit_code || baseMapping.symbol || baseMapping.label) : (option.unitOfMeasure || '');
        const defaultUomId = baseMapping ? (baseMapping.uom_id || baseMapping.id) : (option.uom_id || option.base_uom_id || null);

        const prodName = option.name || (option.label ? option.label.split(' | ')[0] : 'Product');
        const prodCode = option.product_code || (option.label?.includes('Code: ') ? option.label.split('Code: ')[1]?.split(' | ')[0] : '');

        const updatedRow = {
            ...current,
            value: option.value ?? null,
            name: prodName,
            product_code: prodCode,
            label: option.label ?? null,
            quantityLeft: option.quantityLeft ?? null,
            unitOfMeasure: defaultUom,
            uom_id: defaultUomId ? Number(defaultUomId) : null,
            uom_mappings: mappings,
            base_uom_id: option.base_uom_id || (baseMapping ? (baseMapping.uom_id || baseMapping.id) : null),
            base_unit_of_measure: defaultUom,
            is_from_requisition: false,
            quantity: current.quantity === '' || current.quantity == null ? 1 : current.quantity,
            meta_data: option.meta_data || null,
        };

        update(index, updatedRow);

        clearErrors(`selectedProducts.${index}.quantity`);
        clearErrors(`selectedProducts.${index}.unitOfMeasure`);
    };

    const onSubmit = async (data) => {
        if (!data.selectedProducts.length) {
            LoginUtility.toastNotifications(
                isFromRequisitionMode ? 'No products present for transfer.' : 'Please add at least one product.',
                "Error",
                "error"
            );
            return;
        }

        try {
            const formattedData = {
                from_store_id: data.fromStore.value,
                to_store_id: data.toStore.value,
                notes: data.notes,
                products: data.selectedProducts
                    .filter((el) => el.value && Number(el.quantityLeft) > 0)
                    .map((el) => {
                        let resolvedUomId = el.uom_id ? Number(el.uom_id) : null;
                        if (!resolvedUomId && Array.isArray(el.uom_mappings) && el.uom_mappings.length > 0) {
                            const matched = el.uom_mappings.find(
                                (u) => (u.unit_code || u.symbol || u.label) === el.unitOfMeasure
                            );
                            if (matched) {
                                resolvedUomId = Number(matched.uom_id || matched.id);
                            }
                        }
                        if (!resolvedUomId && Array.isArray(measurementUnits) && measurementUnits.length > 0) {
                            const matchedGlobal = measurementUnits.find(
                                (u) => u.unit_code === el.unitOfMeasure || u.label === el.unitOfMeasure
                            );
                            if (matchedGlobal && (matchedGlobal.id || matchedGlobal.uom_id)) {
                                resolvedUomId = Number(matchedGlobal.id || matchedGlobal.uom_id);
                            }
                        }

                        return {
                            slack: el.value,
                            name: el.label || el.name,
                            quantity: Number(el.quantity) || 1,
                            quantity_left: el.quantityLeft,
                            unit_of_measure: el.unitOfMeasure || '',
                            uom_id: resolvedUomId,
                        };
                    }),
            };

            // Add requisition fields if available
            if (requisitionReference) {
                formattedData.stock_transfer_reference = requisitionReference;
            }
            if (requisitionId) {
                formattedData.requisition_id = requisitionId;
            }

            if (!formattedData.products.length) {
                toast.error('Please select at least one product.');
                return;
            }

            if (isEdit) {
                const response = await updateStockTransfer(slack, formattedData);
                if (response.success) {
                    LoginUtility.toastNotifications(response.message, "Success", "success");
                    history.push(`/${localStorage.getItem('i18nextLng') || 'en'}/stock/transfer/list`);
                }
            } else {
                formattedData.store_id = Number(localStorage.getItem('store_id'))
                const response = await createStockTransfer(formattedData);
                if (response.success) {
                    LoginUtility.toastNotifications(response.message, "Success", "success");
                    history.push(`/${localStorage.getItem('i18nextLng') || 'en'}/stock/transfer/list`);
                }
            }
        } catch (e) {
            console.error('Error saving stock transfer:', e);
        }
    };

    useEffect(() => { if (isEdit && slack) fetchStockTransfer(); }, [isEdit, slack]); // eslint-disable-line

    // Pre-fill form from requisition data if available
    useEffect(() => {
        if (!isEdit) {
            const requisitionData = localStorage.getItem('stockTransferFromRequisition');
            if (requisitionData) {
                try {
                    const data = JSON.parse(requisitionData);

                    // Set requisition reference and slack
                    setRequisitionReference(data.stock_transfer_reference || '');
                    setRequisitionSlack(data.requisition_slack || '');
                    setRequisitionId(data.requisition_id || '');

                    // Use store details from requisition data directly
                    const selectedProducts = data.products.map(p => {
                        const prodName = p.name || p.Product?.name || 'Product';
                        const prodCode = p.product_code || p.Product?.product_code || '';
                        const parts = [prodName];
                        if (prodCode) parts.push(`Code: ${prodCode}`);
                        if (p.quantity_left !== undefined && p.quantity_left !== null) {
                            parts.push(`Qty: ${p.quantity_left}${p.unit_of_measure ? ` ${p.unit_of_measure}` : ''}`);
                        }

                        const uomMappings = Array.isArray(p.uom_mappings) && p.uom_mappings.length > 0
                            ? p.uom_mappings
                            : (Array.isArray(p.Product?.uom_mappings) && p.Product.uom_mappings.length > 0 ? p.Product.uom_mappings : []);

                        const reqUomId = p.requested_uom_id || p.uom_id;
                        const reqUomCode = p.requested_unit_of_measure || p.unit_of_measure;
                        const baseUomId = p.base_uom_id || p.Product?.base_uom_id;
                        const baseUomCode = p.base_unit_of_measure || p.Product?.unit_of_measure;

                        const initialUomCode = reqUomCode || baseUomCode || p.unit_of_measure || p.Product?.unit_of_measure || '';
                        const initialUomId = reqUomId || baseUomId || p.uom_id || null;

                        return {
                            value: p.slack || p.Product?.slack,
                            name: prodName,
                            product_code: prodCode,
                            label: parts.join(' | '),
                            quantity: p.quantity || p.quantity_requested || 1,
                            quantityLeft: p.quantity_left,
                            unitOfMeasure: initialUomCode,
                            uom_id: initialUomId ? Number(initialUomId) : null,
                            requested_uom_id: reqUomId ? Number(reqUomId) : null,
                            requested_unit_of_measure: reqUomCode,
                            quantity_requested: p.quantity_requested,
                            uom_mappings: uomMappings,
                            base_uom_id: baseUomId ? Number(baseUomId) : null,
                            base_unit_of_measure: baseUomCode,
                            meta_data: p.meta_data || p.Product?.meta_data || [],
                            is_from_requisition: true,
                        };
                    });

                    reset({
                        fromStore: data.from_store_details ? {
                            label: data.from_store_details.name,
                            value: data.from_store_details.id,
                            store_code: data.from_store_details.store_code,
                        } : null,
                        toStore: data.to_store_details ? {
                            label: data.to_store_details.name,
                            value: data.to_store_details.id,
                            store_code: data.to_store_details.store_code,
                        } : null,
                        notes: data.notes,
                        selectedProducts: selectedProducts,
                        productSearch: null,
                    });

                    // Clear the localStorage after pre-filling
                    localStorage.removeItem('stockTransferFromRequisition');
                } catch (error) {
                    console.error('Error parsing requisition data:', error);
                    localStorage.removeItem('stockTransferFromRequisition');
                }
            }
        }
    }, [isEdit, reset]); // eslint-disable-line

    // Helper function to get meta data value
    const getFromMetaData = (meta_data, key) => {
        if (!meta_data || !Array.isArray(meta_data)) return null;
        const metaItem = meta_data.find(item => item.meta_key === key);
        return metaItem ? metaItem.meta_value : null;
    };

    // Helper function to get UOM conversion factor for a row
    const getUomConversionFactor = (row) => {
        if (!row) return 1;
        return LoginUtility.getUomConversionFactor(row.uom_mappings, row.uom_id || row.unitOfMeasure);
    };

    return (
        <div className="stock-transfer-form-wrapper" style={{ padding: '0 0 12px 0' }}>
            <Card
                className="shadow-sm border-0"
                style={{
                    borderRadius: 10,
                    overflow: 'hidden',
                    backgroundColor: '#ffffff',
                    border: '1px solid #e2e8f0',
                }}
            >
                {/* Header Section */}
                <div
                    style={{
                        padding: '10px 16px',
                        borderBottom: '1px solid #f1f5f9',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        backgroundColor: '#ffffff',
                    }}
                >
                    <div className="d-flex align-items-center gap-2">
                        <div
                            style={{
                                width: 34,
                                height: 34,
                                borderRadius: 8,
                                background: 'linear-gradient(135deg, #2563eb, #1d4ed8)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: '#ffffff',
                                boxShadow: '0 2px 6px rgba(37, 99, 235, 0.2)',
                            }}
                        >
                            <FontAwesomeIcon icon={faExchangeAlt} />
                        </div>
                        <div>
                            <h6 style={{ margin: 0, fontWeight: 700, color: '#0f172a', fontSize: '1.05rem', lineHeight: '1.2' }}>
                                {isEdit ? 'Edit Stock Transfer' : (isFromRequisitionMode ? 'Stock Transfer (From Requisition)' : 'Add Stock Transfer')}
                            </h6>
                            <p style={{ margin: 0, fontSize: '0.78rem', color: '#64748b', lineHeight: '1.2' }}>
                                {isFromRequisitionMode
                                    ? `Fulfilling requisition items from ${watchedRows?.length || 0} requested product(s)`
                                    : 'Transfer products between stores with real-time stock & unit mapping'}
                            </p>
                        </div>
                    </div>
                    <div>
                        <Link
                            to={`/${localStorage.getItem('i18nextLng') || 'en'}/stock/transfer/list`}
                            className="btn btn-outline-secondary btn-sm"
                            style={{
                                borderRadius: 6,
                                padding: '4px 10px',
                                fontWeight: 600,
                                fontSize: '0.8rem',
                                borderColor: '#cbd5e1',
                                color: '#475569',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 5,
                            }}
                        >
                            <FontAwesomeIcon icon={faArrowLeft} />
                            Back
                        </Link>
                    </div>
                </div>

                <Card.Body style={{ padding: '12px 16px' }}>
                    <Form noValidate onSubmit={handleSubmit(onSubmit)}>
                        {/* Requisition Reference Banner (if from requisition) */}
                        {isFromRequisitionMode && (
                            <div
                                className="mb-2"
                                style={{
                                    backgroundColor: '#eff6ff',
                                    border: '1px solid #bfdbfe',
                                    borderRadius: 8,
                                    padding: '8px 14px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                }}
                            >
                                <div className="d-flex align-items-center gap-2">
                                    <div
                                        style={{
                                            width: 28,
                                            height: 28,
                                            borderRadius: 6,
                                            backgroundColor: '#dbeafe',
                                            color: '#1d4ed8',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            fontSize: '0.85rem',
                                        }}
                                    >
                                        <FontAwesomeIcon icon={faClipboardList} />
                                    </div>
                                    <div>
                                        <span style={{ fontSize: '0.78rem', fontWeight: 600, color: '#475569', marginRight: 6 }}>
                                            Requisition Reference:
                                        </span>
                                        <span style={{ fontSize: '0.95rem', fontWeight: 700, color: '#1e3a8a' }}>
                                            {requisitionReference}
                                        </span>
                                    </div>
                                </div>
                                <span
                                    className="badge"
                                    style={{
                                        backgroundColor: '#2563eb',
                                        color: '#ffffff',
                                        borderRadius: 4,
                                        padding: '4px 8px',
                                        fontSize: '0.72rem',
                                        fontWeight: 600,
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: 4,
                                    }}
                                >
                                    <FontAwesomeIcon icon={faInfoCircle} />
                                    Requisition Mode
                                </span>
                            </div>
                        )}

                        {/* Section 1: Store & Transfer Route */}
                        <div
                            style={{
                                backgroundColor: '#f8fafc',
                                border: '1px solid #e2e8f0',
                                borderRadius: 8,
                                padding: '10px 12px',
                                marginBottom: 10,
                            }}
                        >
                            <div className="d-flex align-items-center gap-2 mb-2">
                                <FontAwesomeIcon icon={faStore} style={{ color: '#2563eb', fontSize: '0.85rem' }} />
                                <span style={{ fontWeight: 700, fontSize: '0.85rem', color: '#1e293b' }}>
                                    Store &amp; Transfer Route
                                </span>
                            </div>

                            <Row className="g-2">
                                <Col md={6}>
                                    <Form.Group controlId="fromStore">
                                        <Form.Label style={{ fontSize: '0.8rem', fontWeight: 600, color: '#475569', marginBottom: 3 }}>
                                            From Store <span className="badge bg-secondary ms-1" style={{ fontSize: '0.65rem', padding: '2px 5px', borderRadius: 3 }}>Source</span>
                                        </Form.Label>
                                        <FieldWithUnderError error={errors.fromStore?.message}>
                                            <Controller
                                                control={control}
                                                name="fromStore"
                                                render={({ field }) => (
                                                    <AsyncSelect
                                                        {...field}
                                                        isDisabled
                                                        cacheOptions
                                                        loadOptions={fetchStoreOptions}
                                                        placeholder="From store"
                                                        styles={selectStyles(false)}
                                                        menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                                                    />
                                                )}
                                            />
                                        </FieldWithUnderError>
                                    </Form.Group>
                                </Col>

                                <Col md={6}>
                                    <Form.Group controlId="toStore">
                                        <Form.Label style={{ fontSize: '0.8rem', fontWeight: 600, color: '#475569', marginBottom: 3 }}>
                                            To Store <span style={{ color: '#dc3545' }}>*</span> <span className="badge bg-primary ms-1" style={{ fontSize: '0.65rem', padding: '2px 5px', borderRadius: 3 }}>Destination</span>
                                        </Form.Label>
                                        <Controller
                                            control={control}
                                            name="toStore"
                                            rules={{ required: 'To Store is required' }}
                                            render={({ field }) => {
                                                const err = errors.toStore?.message;
                                                return (
                                                    <FieldWithUnderError error={err}>
                                                        <AsyncSelect
                                                            {...field}
                                                            isDisabled={isFromRequisitionMode && !!getValues('toStore')}
                                                            cacheOptions
                                                            loadOptions={debouncedFetchStoreOptions}
                                                            onChange={(opt) => field.onChange(opt)}
                                                            placeholder="Type Store Name to select destination"
                                                            isClearable={!isFromRequisitionMode}
                                                            styles={selectStyles(!!err)}
                                                            menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                                                        />
                                                    </FieldWithUnderError>
                                                );
                                            }}
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>
                        </div>

                        {/* Section 2: Products Adder and Item Rows */}
                        <div
                            style={{
                                backgroundColor: '#ffffff',
                                border: '1px solid #e2e8f0',
                                borderRadius: 8,
                                padding: '10px 12px',
                                marginBottom: 10,
                            }}
                        >
                            <div className="d-flex align-items-center justify-content-between mb-2">
                                <div className="d-flex align-items-center gap-2">
                                    <FontAwesomeIcon icon={faBoxes} style={{ color: '#2563eb', fontSize: '0.85rem' }} />
                                    <span style={{ fontWeight: 700, fontSize: '0.85rem', color: '#1e293b' }}>
                                        {isFromRequisitionMode ? 'Requisition Products to Transfer' : 'Transfer Items'}
                                    </span>
                                </div>
                                <span style={{ fontSize: '0.75rem', color: '#64748b' }}>
                                    {fields.length} {fields.length === 1 ? 'item' : 'items'}
                                </span>
                            </div>

                            {/* Top product adder - Only in Manual Mode */}
                            {!isFromRequisitionMode && (
                                <Row className="mb-2">
                                    <Col md={6}>
                                        <Form.Group controlId="productSearch">
                                            <Form.Label style={{ fontSize: '0.8rem', fontWeight: 600, color: '#475569', marginBottom: 3 }}>
                                                Quick Search &amp; Add Products
                                            </Form.Label>
                                            <FieldWithUnderError error={errors.selectedProducts?.message}>
                                                <Controller
                                                    control={control}
                                                    name="productSearch"
                                                    render={({ field }) => (
                                                        <AsyncSelect
                                                            {...field}
                                                            cacheOptions={false}
                                                            loadOptions={debouncedFetchProductOptions}
                                                            onChange={(opt) => handleProductPickedFromTop(opt)}
                                                            placeholder="Type product name or code (min 3 chars)..."
                                                            isClearable
                                                            value={null}
                                                            styles={selectStyles(false)}
                                                            menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                                                        />
                                                    )}
                                                />
                                            </FieldWithUnderError>
                                        </Form.Group>
                                    </Col>
                                </Row>
                            )}

                            {/* Column Header Bar */}
                            <div
                                style={{
                                    backgroundColor: '#f8fafc',
                                    border: '1px solid #e2e8f0',
                                    borderRadius: 6,
                                    padding: '6px 10px',
                                    marginBottom: 6,
                                    display: 'none',
                                }}
                                className="d-md-block"
                            >
                                <Row className="align-items-center g-2" style={{ fontSize: '0.72rem', fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                    {isFromRequisitionMode ? (
                                        <>
                                            <Col md={4}>Product Details</Col>
                                            <Col md={2}>Requested Qty</Col>
                                            <Col md={2}>Transfer Unit (UOM)</Col>
                                            <Col md={2}>Transfer Qty</Col>
                                            <Col md={2}>Available Stock</Col>
                                        </>
                                    ) : (
                                        <>
                                            <Col md={5}>Product Name &amp; Description</Col>
                                            <Col md={3}>Unit of Measure</Col>
                                            <Col md={2}>Transfer Qty</Col>
                                            <Col md={2}>Total Stock Left</Col>
                                        </>
                                    )}
                                </Row>
                            </div>

                            {/* Dynamic product rows */}
                            {fields.length > 0 && (
                                <div className="product-rows-container">
                                    {fields.map((el, i) => {
                                        // use *watched* row for live values after remove/append/update
                                        const row = watchedRows?.[i] || {};
                                        const productErr = errors.selectedProducts?.[i]?.value?.message;
                                        const uomErr = errors.selectedProducts?.[i]?.unitOfMeasure?.message;
                                        const qtyErr = errors.selectedProducts?.[i]?.quantity?.message;

                                        const prodName = row?.name || (row?.label ? row.label.split(' | ')[0] : 'Product');
                                        const prodCode = row?.product_code || (row?.label?.includes('Code: ') ? row.label.split('Code: ')[1]?.split(' | ')[0] : '');
                                        const requestedQty = row?.quantity_requested !== undefined && row?.quantity_requested !== null ? row.quantity_requested : null;
                                        const requestedUnit = row?.requested_unit_of_measure || '';

                                        return (
                                            <div
                                                key={el.key}
                                                style={{
                                                    backgroundColor: '#ffffff',
                                                    border: '1px solid #e2e8f0',
                                                    borderRadius: 6,
                                                    padding: '8px 10px',
                                                    marginBottom: 6,
                                                    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.02)',
                                                }}
                                            >
                                                {/* Requisition Mode Row Layout */}
                                                {isFromRequisitionMode ? (
                                                    <Row className="align-items-center g-2">
                                                        {/* Product Details Display */}
                                                        <Col md={4}>
                                                            <div>
                                                                <div style={{ fontWeight: 700, color: '#0f172a', fontSize: '0.875rem' }}>
                                                                    {prodName}
                                                                </div>
                                                                {prodCode && (
                                                                    <div className="mt-1">
                                                                        <span className="badge bg-light text-secondary border" style={{ fontSize: '0.7rem', fontWeight: 600 }}>
                                                                            Code: {prodCode}
                                                                        </span>
                                                                    </div>
                                                                )}

                                                                {/* Hidden input to keep react-hook-form value */}
                                                                <Controller
                                                                    control={control}
                                                                    name={`selectedProducts.${i}.value`}
                                                                    render={({ field }) => <input type="hidden" {...field} />}
                                                                />

                                                                {/* Meta Information Display - Tag Chips */}
                                                                {LoginUtility.isNotRestaurantStore(currentStore) && row?.meta_data && row.meta_data.length > 0 && (
                                                                    <div style={{ marginTop: 4, display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                                                                        {row.meta_data.map((meta, metaIndex) => {
                                                                            const displayLabels = {
                                                                                'generic_name': 'Generic',
                                                                                'hsn_code': 'HSN',
                                                                                'drug_schedule': 'Schedule',
                                                                                'mfg_date': 'Mfg',
                                                                                'exp_date': 'Exp',
                                                                                'uom': 'UOM',
                                                                                'mrp': 'MRP',
                                                                                'manufacturer_name': 'Mfr',
                                                                                'requires_prescription': 'Rx'
                                                                            };

                                                                            const label = displayLabels[meta.meta_key] || meta.meta_key;
                                                                            const value = meta.meta_key === 'mrp' ? `₹${meta.meta_value}` :
                                                                                meta.meta_key === 'requires_prescription' && meta.meta_value === 'true' ? 'Required' :
                                                                                    meta.meta_key === 'requires_prescription' && meta.meta_value === 'false' ? 'No' :
                                                                                        meta.meta_value;

                                                                            const isRx = meta.meta_key === 'requires_prescription' && meta.meta_value === 'true';

                                                                            return (
                                                                                <span
                                                                                    key={metaIndex}
                                                                                    style={{
                                                                                        display: 'inline-flex',
                                                                                        alignItems: 'center',
                                                                                        backgroundColor: isRx ? '#fef2f2' : '#f8fafc',
                                                                                        border: `1px solid ${isRx ? '#fecaca' : '#e2e8f0'}`,
                                                                                        borderRadius: 4,
                                                                                        padding: '1px 5px',
                                                                                        fontSize: '10px',
                                                                                        color: isRx ? '#dc2626' : '#334155',
                                                                                    }}
                                                                                >
                                                                                    <span style={{ color: isRx ? '#ef4444' : '#64748b', fontWeight: 500, marginRight: 3 }}>
                                                                                        {label}:
                                                                                    </span>
                                                                                    <span style={{ fontWeight: 600 }}>
                                                                                        {value}
                                                                                    </span>
                                                                                </span>
                                                                            );
                                                                        })}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </Col>

                                                        {/* Requested Quantity Badge */}
                                                        <Col md={2}>
                                                            <div className="d-md-none mb-1">
                                                                <span style={{ fontSize: '0.75rem', fontWeight: 600, color: '#475569' }}>Requested Qty: </span>
                                                            </div>
                                                            <div
                                                                style={{
                                                                    backgroundColor: '#eff6ff',
                                                                    border: '1px solid #bfdbfe',
                                                                    borderRadius: 6,
                                                                    padding: '4px 8px',
                                                                    display: 'inline-flex',
                                                                    alignItems: 'center',
                                                                    gap: 5,
                                                                }}
                                                            >
                                                                <span style={{ fontSize: '0.85rem', fontWeight: 700, color: '#1e40af' }}>
                                                                    {requestedQty !== null ? Number(requestedQty) : (Number(row.quantity) || '-')}
                                                                </span>
                                                                <span style={{ fontSize: '0.75rem', fontWeight: 600, color: '#3b82f6' }}>
                                                                    {requestedUnit || row.unitOfMeasure || ''}
                                                                </span>
                                                            </div>
                                                        </Col>

                                                        {/* Transfer Unit of Measure */}
                                                        <Col md={2}>
                                                            <div className="d-md-none mb-1">
                                                                <Form.Label style={{ fontSize: '0.75rem', fontWeight: 600, color: '#475569' }}>Transfer Unit</Form.Label>
                                                            </div>
                                                            <FieldWithUnderError error={uomErr}>
                                                                <Controller
                                                                    control={control}
                                                                    name={`selectedProducts.${i}.unitOfMeasure`}
                                                                    rules={{
                                                                        validate: () => {
                                                                            const currentRow = getValues(`selectedProducts.${i}`);
                                                                            if (!currentRow?.value) return true;
                                                                            if (!currentRow.unitOfMeasure) {
                                                                                return 'Unit is required';
                                                                            }
                                                                            return true;
                                                                        }
                                                                    }}
                                                                    render={({ field }) => {
                                                                        const mappings = Array.isArray(row?.uom_mappings) && row.uom_mappings.length > 0
                                                                            ? row.uom_mappings
                                                                            : [];
                                                                        const isFromReq = true;
                                                                        const requestedUomId = row?.requested_uom_id ? Number(row?.requested_uom_id) : null;
                                                                        const requestedUomCode = row?.requested_unit_of_measure;
                                                                        const baseUomId = row?.base_uom_id ? Number(row?.base_uom_id) : null;
                                                                        const baseUomCode = row?.base_unit_of_measure;

                                                                        return (
                                                                            <Form.Select
                                                                                value={field.value || ''}
                                                                                onChange={(e) => {
                                                                                    const selectedCode = e.target.value;
                                                                                    field.onChange(selectedCode);

                                                                                    const matchedMapping = mappings.find(
                                                                                        (u) => (u.unit_code || u.symbol || u.label) === selectedCode
                                                                                    );
                                                                                    const matchedGlobal = measurementUnits.find(
                                                                                        (u) => u.unit_code === selectedCode || u.label === selectedCode
                                                                                    );
                                                                                    const resolvedId = matchedMapping
                                                                                        ? (matchedMapping.uom_id || matchedMapping.id)
                                                                                        : (matchedGlobal ? (matchedGlobal.id || matchedGlobal.uom_id) : null);

                                                                                    setValue(`selectedProducts.${i}.uom_id`, resolvedId ? Number(resolvedId) : null);
                                                                                    trigger(`selectedProducts.${i}.quantity`);
                                                                                }}
                                                                                style={{
                                                                                    height: FIELD_HEIGHT,
                                                                                    borderRadius: 6,
                                                                                    fontSize: '0.825rem',
                                                                                    borderColor: uomErr ? '#dc3545' : '#cbd5e1',
                                                                                    boxShadow: uomErr ? '0 0 0 .2rem rgba(220,53,69,.2)' : 'none',
                                                                                    color: '#1e293b',
                                                                                    fontWeight: 500,
                                                                                    padding: '2px 8px',
                                                                                }}
                                                                                disabled={!row?.value}
                                                                            >
                                                                                <option value="">Select Unit</option>
                                                                                {mappings.length > 0 ? (
                                                                                    mappings.map((u) => {
                                                                                        const code = u.unit_code || u.symbol || u.label;
                                                                                        const uomId = Number(u.uom_id || u.id);
                                                                                        const isBase = u.is_base === 1 || u.is_base === true || (baseUomId && uomId === baseUomId) || (baseUomCode && code === baseUomCode);
                                                                                        const isRequested = isFromReq && ((requestedUomId && uomId === requestedUomId) || (requestedUomCode && code === requestedUomCode));

                                                                                        let tag = '';
                                                                                        if (isBase && isRequested) {
                                                                                            tag = ' (Base - Requested)';
                                                                                        } else if (isBase) {
                                                                                            tag = ' (Base)';
                                                                                        } else if (isRequested) {
                                                                                            tag = ' (Requested)';
                                                                                        }

                                                                                        return (
                                                                                            <option key={u.id || u.uom_id || code} value={code}>
                                                                                                {u.label || code} ({code}){tag}
                                                                                            </option>
                                                                                        );
                                                                                    })
                                                                                ) : (
                                                                                    measurementUnits.map((u) => (
                                                                                        <option key={u.id || u.slack} value={u.unit_code}>
                                                                                            {u.label} ({u.unit_code})
                                                                                        </option>
                                                                                    ))
                                                                                )}
                                                                            </Form.Select>
                                                                        );
                                                                    }}
                                                                />
                                                            </FieldWithUnderError>
                                                        </Col>

                                                        {/* Transfer Quantity */}
                                                        <Col md={2}>
                                                            <div className="d-md-none mb-1">
                                                                <Form.Label style={{ fontSize: '0.75rem', fontWeight: 600, color: '#475569' }}>Transfer Qty</Form.Label>
                                                            </div>
                                                            <FieldWithUnderError error={qtyErr}>
                                                                <Controller
                                                                    control={control}
                                                                    name={`selectedProducts.${i}.quantity`}
                                                                    rules={{
                                                                        validate: () => {
                                                                            const currentRow = getValues(`selectedProducts.${i}`);
                                                                            if (!currentRow?.value) return true;

                                                                            if (currentRow.quantity === '' || currentRow.quantity == null) {
                                                                                return 'Quantity is required';
                                                                            }

                                                                            const n = typeof currentRow.quantity === 'number'
                                                                                ? currentRow.quantity
                                                                                : parseFloat(currentRow.quantity);

                                                                            if (!Number.isFinite(n)) {
                                                                                return 'Must be a valid number';
                                                                            }
                                                                            if (n <= 0) {
                                                                                return 'Enter a number > 0';
                                                                            }

                                                                            const totalStockLeftBase = Number(currentRow.quantityLeft) || 0;
                                                                            const conversionFactor = getUomConversionFactor(currentRow);
                                                                            const maxAllowedInSelectedUom = conversionFactor > 0 ? totalStockLeftBase / conversionFactor : totalStockLeftBase;
                                                                            const cleanMax = parseFloat(maxAllowedInSelectedUom.toFixed(6));

                                                                            if (n * conversionFactor > totalStockLeftBase + 0.000001) {
                                                                                return `Max: ${cleanMax}`;
                                                                            }

                                                                            return true;
                                                                        },
                                                                        required: 'Quantity is required'
                                                                    }}
                                                                    render={({ field }) => (
                                                                        <input
                                                                            type="number"
                                                                            inputMode="decimal"
                                                                            step="0.01"
                                                                            min="0.01"
                                                                            value={field.value ?? ''}
                                                                            onChange={(e) => {
                                                                                const { value } = e.target;
                                                                                const parsed = value === '' ? '' : Number(value);
                                                                                field.onChange(Number.isFinite(parsed) ? parsed : value);
                                                                            }}
                                                                            style={{
                                                                                width: '100%',
                                                                                height: FIELD_HEIGHT,
                                                                                borderRadius: 6,
                                                                                border: `1px solid ${qtyErr ? '#dc3545' : '#cbd5e1'}`,
                                                                                padding: '4px 8px',
                                                                                outline: 'none',
                                                                                boxSizing: 'border-box',
                                                                                boxShadow: qtyErr ? '0 0 0 .2rem rgba(220,53,69,.2)' : 'none',
                                                                                fontWeight: 600,
                                                                                color: '#0f172a',
                                                                                fontSize: '0.85rem',
                                                                            }}
                                                                        />
                                                                    )}
                                                                />
                                                            </FieldWithUnderError>
                                                        </Col>

                                                        {/* Total Stock Left & Remove */}
                                                        <Col md={2}>
                                                            <div className="d-flex align-items-center justify-content-between" style={{ height: FIELD_HEIGHT }}>
                                                                <div>
                                                                    {Number(row?.quantityLeft) > 0 ? (
                                                                        <div
                                                                            style={{
                                                                                backgroundColor: '#ecfdf5',
                                                                                color: '#059669',
                                                                                border: '1px solid #a7f3d0',
                                                                                borderRadius: 4,
                                                                                padding: '4px 8px',
                                                                                fontSize: '0.75rem',
                                                                                fontWeight: 600,
                                                                                display: 'inline-flex',
                                                                                alignItems: 'center',
                                                                                gap: 4,
                                                                            }}
                                                                            title={`Available in Source Store: ${Number(row?.quantityLeft) || 0} ${row?.base_unit_of_measure || row?.unitOfMeasure || ''}`}
                                                                        >
                                                                            <FontAwesomeIcon icon={faCheckCircle} />
                                                                            <span>
                                                                                {Number(row?.quantityLeft) || 0} {row?.base_unit_of_measure || row?.unitOfMeasure || ''}
                                                                            </span>
                                                                        </div>
                                                                    ) : Number(row?.quantityLeft) === 0 ? (
                                                                        <div
                                                                            style={{
                                                                                backgroundColor: '#fef2f2',
                                                                                color: '#dc2626',
                                                                                border: '1px solid #fecaca',
                                                                                borderRadius: 4,
                                                                                padding: '4px 8px',
                                                                                fontSize: '0.72rem',
                                                                                fontWeight: 600,
                                                                                display: 'inline-flex',
                                                                                alignItems: 'center',
                                                                                gap: 4,
                                                                            }}
                                                                        >
                                                                            <FontAwesomeIcon icon={faExclamationTriangle} />
                                                                            <span>0 stock</span>
                                                                        </div>
                                                                    ) : (
                                                                        <span style={{ color: '#94a3b8', fontSize: '0.78rem' }}>-</span>
                                                                    )}
                                                                </div>

                                                                {/* Remove button — allowed in requisition mode */}
                                                                <Button
                                                                    variant="light"
                                                                    size="sm"
                                                                    onClick={() => remove(i)}
                                                                    title="Remove product"
                                                                    style={{
                                                                        width: 28,
                                                                        height: 28,
                                                                        padding: 0,
                                                                        borderRadius: 4,
                                                                        color: '#ef4444',
                                                                        backgroundColor: '#fef2f2',
                                                                        border: '1px solid #fee2e2',
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        justifyContent: 'center',
                                                                        fontSize: '0.75rem',
                                                                    }}
                                                                >
                                                                    <FontAwesomeIcon icon={faClose} />
                                                                </Button>
                                                            </div>
                                                        </Col>
                                                    </Row>
                                                ) : (
                                                    /* Manual / Standard Transfer Row Layout */
                                                    <Row className="align-items-start g-2">
                                                        {/* Product select or product card */}
                                                        <Col md={5}>
                                                            <Form.Group>
                                                                <div className="d-md-none mb-1">
                                                                    <Form.Label style={{ fontSize: '0.75rem', fontWeight: 600, color: '#475569' }}>Product</Form.Label>
                                                                </div>

                                                                {row?.value ? (
                                                                    <div
                                                                        style={{
                                                                            border: '1px solid #e2e8f0',
                                                                            borderRadius: 6,
                                                                            padding: '6px 8px',
                                                                            backgroundColor: '#f8fafc',
                                                                            display: 'flex',
                                                                            alignItems: 'center',
                                                                            justifyContent: 'space-between',
                                                                            minHeight: FIELD_HEIGHT,
                                                                        }}
                                                                    >
                                                                        <div>
                                                                            <span style={{ fontWeight: 700, color: '#0f172a', fontSize: '0.85rem' }}>
                                                                                {prodName}
                                                                            </span>
                                                                            {prodCode && (
                                                                                <span className="badge bg-white text-secondary border ms-2" style={{ fontSize: '0.7rem' }}>
                                                                                    {prodCode}
                                                                                </span>
                                                                            )}
                                                                        </div>
                                                                        {/* <Button
                                                                            variant="link"
                                                                            size="sm"
                                                                            onClick={() => applyRowProduct(null, i)}
                                                                            style={{ padding: '0 4px', fontSize: '0.75rem', color: '#64748b' }}
                                                                            title="Change product"
                                                                        >
                                                                            <FontAwesomeIcon icon={faEdit} />
                                                                        </Button> */}

                                                                        <Controller
                                                                            control={control}
                                                                            name={`selectedProducts.${i}.value`}
                                                                            rules={{ required: 'Product is required' }}
                                                                            render={({ field }) => <input type="hidden" {...field} />}
                                                                        />
                                                                    </div>
                                                                ) : (
                                                                    <FieldWithUnderError error={productErr}>
                                                                        <Controller
                                                                            control={control}
                                                                            name={`selectedProducts.${i}.value`}
                                                                            rules={{ required: 'Name is required' }}
                                                                            render={({ field }) => (
                                                                                <AsyncSelect
                                                                                    cacheOptions={false}
                                                                                    loadOptions={debouncedFetchProductOptions}
                                                                                    placeholder="Search and select product..."
                                                                                    isClearable
                                                                                    value={null}
                                                                                    onChange={(opt) => {
                                                                                        field.onChange(opt?.value ?? null);
                                                                                        applyRowProduct(opt, i);
                                                                                    }}
                                                                                    styles={selectStyles(!!productErr)}
                                                                                    menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                                                                                />
                                                                            )}
                                                                        />
                                                                    </FieldWithUnderError>
                                                                )}
                                                            </Form.Group>

                                                            {/* Meta Information Display - Tag Chips */}
                                                            {LoginUtility.isNotRestaurantStore(currentStore) && row?.value && row?.meta_data && row.meta_data.length > 0 && (
                                                                <div style={{ marginTop: 4, display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                                                                    {row.meta_data.map((meta, metaIndex) => {
                                                                        const displayLabels = {
                                                                            'generic_name': 'Generic',
                                                                            'hsn_code': 'HSN',
                                                                            'drug_schedule': 'Schedule',
                                                                            'mfg_date': 'Mfg',
                                                                            'exp_date': 'Exp',
                                                                            'uom': 'UOM',
                                                                            'mrp': 'MRP',
                                                                            'manufacturer_name': 'Mfr',
                                                                            'requires_prescription': 'Rx'
                                                                        };

                                                                        const label = displayLabels[meta.meta_key] || meta.meta_key;
                                                                        const value = meta.meta_key === 'mrp' ? `₹${meta.meta_value}` :
                                                                            meta.meta_key === 'requires_prescription' && meta.meta_value === 'true' ? 'Required' :
                                                                                meta.meta_key === 'requires_prescription' && meta.meta_value === 'false' ? 'No' :
                                                                                    meta.meta_value;

                                                                        const isRx = meta.meta_key === 'requires_prescription' && meta.meta_value === 'true';

                                                                        return (
                                                                            <span
                                                                                key={metaIndex}
                                                                                style={{
                                                                                    display: 'inline-flex',
                                                                                    alignItems: 'center',
                                                                                    backgroundColor: isRx ? '#fef2f2' : '#f8fafc',
                                                                                    border: `1px solid ${isRx ? '#fecaca' : '#e2e8f0'}`,
                                                                                    borderRadius: 4,
                                                                                    padding: '1px 5px',
                                                                                    fontSize: '10px',
                                                                                    color: isRx ? '#dc2626' : '#334155',
                                                                                }}
                                                                            >
                                                                                <span style={{ color: isRx ? '#ef4444' : '#64748b', fontWeight: 500, marginRight: 3 }}>
                                                                                    {label}:
                                                                                </span>
                                                                                <span style={{ fontWeight: 600 }}>
                                                                                    {value}
                                                                                </span>
                                                                            </span>
                                                                        );
                                                                    })}
                                                                </div>
                                                            )}
                                                        </Col>

                                                        {/* Unit of Measure */}
                                                        <Col md={3}>
                                                            <Form.Group>
                                                                <div className="d-md-none mb-1">
                                                                    <Form.Label style={{ fontSize: '0.75rem', fontWeight: 600, color: '#475569' }}>Unit of Measure</Form.Label>
                                                                </div>
                                                                <FieldWithUnderError error={uomErr}>
                                                                    <Controller
                                                                        control={control}
                                                                        name={`selectedProducts.${i}.unitOfMeasure`}
                                                                        rules={{
                                                                            validate: () => {
                                                                                const currentRow = getValues(`selectedProducts.${i}`);
                                                                                if (!currentRow?.value) return true;
                                                                                if (!currentRow.unitOfMeasure) {
                                                                                    return 'Unit is required';
                                                                                }
                                                                                return true;
                                                                            }
                                                                        }}
                                                                        render={({ field }) => {
                                                                            const mappings = Array.isArray(row?.uom_mappings) && row.uom_mappings.length > 0
                                                                                ? row.uom_mappings
                                                                                : [];
                                                                            const isFromReq = false;
                                                                            const requestedUomId = row?.requested_uom_id ? Number(row?.requested_uom_id) : null;
                                                                            const requestedUomCode = row?.requested_unit_of_measure;
                                                                            const baseUomId = row?.base_uom_id ? Number(row?.base_uom_id) : null;
                                                                            const baseUomCode = row?.base_unit_of_measure;

                                                                            return (
                                                                                <Form.Select
                                                                                    value={field.value || ''}
                                                                                    onChange={(e) => {
                                                                                        const selectedCode = e.target.value;
                                                                                        field.onChange(selectedCode);

                                                                                        const matchedMapping = mappings.find(
                                                                                            (u) => (u.unit_code || u.symbol || u.label) === selectedCode
                                                                                        );
                                                                                        const matchedGlobal = measurementUnits.find(
                                                                                            (u) => u.unit_code === selectedCode || u.label === selectedCode
                                                                                        );
                                                                                        const resolvedId = matchedMapping
                                                                                            ? (matchedMapping.uom_id || matchedMapping.id)
                                                                                            : (matchedGlobal ? (matchedGlobal.id || matchedGlobal.uom_id) : null);

                                                                                        setValue(`selectedProducts.${i}.uom_id`, resolvedId ? Number(resolvedId) : null);
                                                                                        trigger(`selectedProducts.${i}.quantity`);
                                                                                    }}
                                                                                    style={{
                                                                                        height: FIELD_HEIGHT,
                                                                                        borderRadius: 6,
                                                                                        fontSize: '0.825rem',
                                                                                        borderColor: uomErr ? '#dc3545' : '#cbd5e1',
                                                                                        boxShadow: uomErr ? '0 0 0 .2rem rgba(220,53,69,.2)' : 'none',
                                                                                        color: '#1e293b',
                                                                                        fontWeight: 500,
                                                                                        padding: '2px 8px',
                                                                                    }}
                                                                                    disabled={!row?.value}
                                                                                >
                                                                                    <option value="">Select Unit</option>
                                                                                    {mappings.length > 0 ? (
                                                                                        mappings.map((u) => {
                                                                                            const code = u.unit_code || u.symbol || u.label;
                                                                                            const uomId = Number(u.uom_id || u.id);
                                                                                            const isBase = u.is_base === 1 || u.is_base === true || (baseUomId && uomId === baseUomId) || (baseUomCode && code === baseUomCode);
                                                                                            const isRequested = isFromReq && ((requestedUomId && uomId === requestedUomId) || (requestedUomCode && code === requestedUomCode));

                                                                                            let tag = '';
                                                                                            if (isBase && isRequested) {
                                                                                                tag = ' (Base Unit - Requested)';
                                                                                            } else if (isBase) {
                                                                                                tag = ' (Base Unit)';
                                                                                            } else if (isRequested) {
                                                                                                tag = ' (Requested Unit)';
                                                                                            }

                                                                                            return (
                                                                                                <option key={u.id || u.uom_id || code} value={code}>
                                                                                                    {u.label || code} ({code}){tag}
                                                                                                </option>
                                                                                            );
                                                                                        })
                                                                                    ) : (
                                                                                        measurementUnits.map((u) => (
                                                                                            <option key={u.id || u.slack} value={u.unit_code}>
                                                                                                {u.label} ({u.unit_code})
                                                                                            </option>
                                                                                        ))
                                                                                    )}
                                                                                </Form.Select>
                                                                            );
                                                                        }}
                                                                    />
                                                                </FieldWithUnderError>
                                                            </Form.Group>
                                                        </Col>

                                                        {/* Quantity */}
                                                        <Col md={2}>
                                                            <Form.Group>
                                                                <div className="d-md-none mb-1">
                                                                    <Form.Label style={{ fontSize: '0.75rem', fontWeight: 600, color: '#475569' }}>Transfer Quantity</Form.Label>
                                                                </div>
                                                                <FieldWithUnderError error={qtyErr}>
                                                                    <Controller
                                                                        control={control}
                                                                        name={`selectedProducts.${i}.quantity`}
                                                                        rules={{
                                                                            validate: () => {
                                                                                const currentRow = getValues(`selectedProducts.${i}`);
                                                                                if (!currentRow?.value) return true;

                                                                                if (currentRow.quantity === '' || currentRow.quantity == null) {
                                                                                    return 'Quantity is required';
                                                                                }

                                                                                const n = typeof currentRow.quantity === 'number'
                                                                                    ? currentRow.quantity
                                                                                    : parseFloat(currentRow.quantity);

                                                                                if (!Number.isFinite(n)) {
                                                                                    return 'Must be a valid number';
                                                                                }
                                                                                if (n <= 0) {
                                                                                    return 'Enter a number > 0';
                                                                                }

                                                                                const totalStockLeftBase = Number(currentRow.quantityLeft) || 0;
                                                                                const conversionFactor = getUomConversionFactor(currentRow);
                                                                                const maxAllowedInSelectedUom = conversionFactor > 0 ? totalStockLeftBase / conversionFactor : totalStockLeftBase;
                                                                                const cleanMax = parseFloat(maxAllowedInSelectedUom.toFixed(6));

                                                                                if (n * conversionFactor > totalStockLeftBase + 0.000001) {
                                                                                    return `Max: ${cleanMax}`;
                                                                                }

                                                                                return true;
                                                                            },
                                                                            required: 'Quantity is required'
                                                                        }}
                                                                        render={({ field }) => (
                                                                            <input
                                                                                type="number"
                                                                                inputMode="decimal"
                                                                                step="0.01"
                                                                                min="0.01"
                                                                                value={field.value ?? ''}
                                                                                onChange={(e) => {
                                                                                    const { value } = e.target;
                                                                                    const parsed = value === '' ? '' : Number(value);
                                                                                    field.onChange(Number.isFinite(parsed) ? parsed : value);
                                                                                }}
                                                                                style={{
                                                                                    width: '100%',
                                                                                    height: FIELD_HEIGHT,
                                                                                    borderRadius: 6,
                                                                                    border: `1px solid ${qtyErr ? '#dc3545' : '#cbd5e1'}`,
                                                                                    padding: '4px 8px',
                                                                                    outline: 'none',
                                                                                    boxSizing: 'border-box',
                                                                                    boxShadow: qtyErr ? '0 0 0 .2rem rgba(220,53,69,.2)' : 'none',
                                                                                    fontWeight: 600,
                                                                                    color: '#0f172a',
                                                                                    fontSize: '0.85rem',
                                                                                }}
                                                                            />
                                                                        )}
                                                                    />
                                                                </FieldWithUnderError>
                                                            </Form.Group>
                                                        </Col>

                                                        {/* Total Stock Left & Action Button */}
                                                        <Col md={2}>
                                                            <div className="d-flex align-items-center justify-content-between" style={{ height: FIELD_HEIGHT }}>
                                                                <div>
                                                                    {Number(row?.quantityLeft) > 0 ? (
                                                                        <div
                                                                            style={{
                                                                                backgroundColor: '#ecfdf5',
                                                                                color: '#059669',
                                                                                border: '1px solid #a7f3d0',
                                                                                borderRadius: 4,
                                                                                padding: '3px 7px',
                                                                                fontSize: '0.75rem',
                                                                                fontWeight: 600,
                                                                                display: 'inline-flex',
                                                                                alignItems: 'center',
                                                                                gap: 4,
                                                                            }}
                                                                            title={`Available: ${Number(row?.quantityLeft) || 0} ${row?.base_unit_of_measure || row?.unitOfMeasure || ''}`}
                                                                        >
                                                                            <FontAwesomeIcon icon={faCheckCircle} />
                                                                            <span>
                                                                                {Number(row?.quantityLeft) || 0} {row?.base_unit_of_measure || row?.unitOfMeasure || ''}
                                                                            </span>
                                                                        </div>
                                                                    ) : row?.value && Number(row?.quantityLeft) === 0 ? (
                                                                        <div
                                                                            style={{
                                                                                backgroundColor: '#fef2f2',
                                                                                color: '#dc2626',
                                                                                border: '1px solid #fecaca',
                                                                                borderRadius: 4,
                                                                                padding: '3px 7px',
                                                                                fontSize: '0.72rem',
                                                                                fontWeight: 600,
                                                                                display: 'inline-flex',
                                                                                alignItems: 'center',
                                                                                gap: 4,
                                                                            }}
                                                                        >
                                                                            <FontAwesomeIcon icon={faExclamationTriangle} />
                                                                            <span>0 stock</span>
                                                                        </div>
                                                                    ) : (
                                                                        <span style={{ color: '#94a3b8', fontSize: '0.78rem' }}>-</span>
                                                                    )}
                                                                </div>

                                                                {/* Remove button */}
                                                                <Button
                                                                    variant="light"
                                                                    size="sm"
                                                                    onClick={() => remove(i)}
                                                                    title="Remove product"
                                                                    style={{
                                                                        width: 28,
                                                                        height: 28,
                                                                        padding: 0,
                                                                        borderRadius: 4,
                                                                        color: '#ef4444',
                                                                        backgroundColor: '#fef2f2',
                                                                        border: '1px solid #fee2e2',
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        justifyContent: 'center',
                                                                        fontSize: '0.75rem',
                                                                    }}
                                                                >
                                                                    <FontAwesomeIcon icon={faClose} />
                                                                </Button>
                                                            </div>
                                                        </Col>
                                                    </Row>
                                                )}

                                                {/* keep quantityLeft, uom_id and meta synced in form state */}
                                                <Controller
                                                    control={control}
                                                    name={`selectedProducts.${i}.quantityLeft`}
                                                    render={({ field }) => <input type="hidden" {...field} />}
                                                />
                                                <Controller
                                                    control={control}
                                                    name={`selectedProducts.${i}.uom_id`}
                                                    render={({ field }) => <input type="hidden" {...field} />}
                                                />
                                            </div>
                                        );
                                    })}
                                </div>
                            )}

                            {/* Add Product button - Only in Manual Mode */}
                            {!isFromRequisitionMode && (
                                <div style={{ marginTop: 8 }}>
                                    <Button
                                        type="button"
                                        onClick={() =>
                                            append({ value: null, name: '', product_code: '', label: null, quantity: '', quantityLeft: null, quantity_requested: null, requested_unit_of_measure: null, unitOfMeasure: '', uom_id: null, uom_mappings: [], is_from_requisition: false })
                                        }
                                        style={{
                                            border: '1.5px dashed #3b82f6',
                                            backgroundColor: '#eff6ff',
                                            color: '#2563eb',
                                            borderRadius: 6,
                                            padding: '4px 12px',
                                            fontWeight: 600,
                                            fontSize: '0.78rem',
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: 6,
                                            transition: 'all 0.15s ease',
                                        }}
                                    >
                                        <FontAwesomeIcon icon={faPlus} />
                                        Add Another Row
                                    </Button>
                                </div>
                            )}
                        </div>

                        {/* Section 3: Notes & Instructions */}
                        <div
                            style={{
                                backgroundColor: '#f8fafc',
                                border: '1px solid #e2e8f0',
                                borderRadius: 8,
                                padding: '10px 12px',
                                marginBottom: 10,
                            }}
                        >
                            <Row>
                                <Col md={8}>
                                    <Form.Group controlId="notes">
                                        <Form.Label style={{ fontSize: '0.8rem', fontWeight: 600, color: '#475569', marginBottom: 3 }}>
                                            Transfer Notes / Remarks
                                        </Form.Label>
                                        <FieldWithUnderError error={errors.notes?.message}>
                                            <Controller
                                                control={control}
                                                name="notes"
                                                render={({ field }) => (
                                                    <Form.Control
                                                        as="textarea"
                                                        rows={2}
                                                        placeholder="Enter any shipment instructions or notes..."
                                                        style={{
                                                            borderRadius: 6,
                                                            borderColor: '#cbd5e1',
                                                            fontSize: '0.825rem',
                                                            padding: '6px 10px',
                                                        }}
                                                        {...field}
                                                    />
                                                )}
                                            />
                                        </FieldWithUnderError>
                                    </Form.Group>
                                </Col>
                            </Row>
                        </div>

                        {/* Section 4: Form Actions Bar */}
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 10,
                                paddingTop: 8,
                                borderTop: '1px solid #e2e8f0',
                            }}
                        >
                            <Button
                                variant="primary"
                                type="submit"
                                disabled={isSubmitting}
                                style={{
                                    background: 'linear-gradient(135deg, #2563eb, #1d4ed8)',
                                    border: 'none',
                                    borderRadius: 6,
                                    color: '#ffffff',
                                    padding: '7px 18px',
                                    fontWeight: 600,
                                    fontSize: '0.85rem',
                                    boxShadow: '0 2px 8px rgba(37, 99, 235, 0.2)',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 6,
                                }}
                            >
                                {isSubmitting ? (
                                    <>
                                        <span
                                            className="spinner-border spinner-border-sm"
                                            role="status"
                                            aria-hidden="true"
                                        ></span>
                                        Saving...
                                    </>
                                ) : (
                                    <>
                                        <FontAwesomeIcon icon={faSave} />
                                        Save Transfer
                                    </>
                                )}
                            </Button>
                            <Button
                                variant="light"
                                type="button"
                                onClick={() => history.goBack()}
                                style={{
                                    border: '1px solid #cbd5e1',
                                    backgroundColor: '#ffffff',
                                    color: '#64748b',
                                    borderRadius: 6,
                                    padding: '7px 16px',
                                    fontWeight: 600,
                                    fontSize: '0.85rem',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 6,
                                }}
                            >
                                <FontAwesomeIcon icon={faTimes} />
                                Cancel
                            </Button>
                        </div>
                    </Form>
                </Card.Body>
            </Card>
        </div>
    );
};

const mapStateToProps = (state) => ({
    currentStore: state.StoreReducer.currentStore,
});

export default connect(mapStateToProps)(StockTransferForm);
